/**
 * What a day shows (1.6.1). One place decides which records are lines on a day, used everywhere a day is drawn:
 * Today, a day ahead, the appointments list, Planning's day, Schedule's day, "Put the day in order".
 *
 * A record that was moved (Rescheduled) or called off (Cancelled) is history: the scheduler keeps it — nothing is
 * deleted — and it shows on the tenant's history and in Look up, not as a line on the day. A day of four visits
 * lists four lines, whatever was moved on the way there.
 */

/** The statuses the day plan places: a visit that is, was or will be at a time on the day. */
export const LIVE_ON_DAY = ['Scheduled', 'In Progress', 'Completed', 'On Hold', 'No Show'] as const;

/** History, never a line on the day. */
export const HISTORY_ONLY = ['Rescheduled', 'Cancelled'] as const;

/**
 * True when a record with this AppointmentStatus is a line on its day. Only Rescheduled and Cancelled are not: a
 * proposal still in pencil (Proposed) and a visit the crew could not finish (Unable to Complete) stay where the
 * screens already show them.
 */
export function isLiveOnDay(status: string | null | undefined): boolean {
  return !(HISTORY_ONLY as readonly string[]).includes(String(status ?? ''));
}

/**
 * The period a line shows, from the time the day plan gives it (minutes after midnight) — not from the imported
 * ServicePeriod column, which the processor keeps for its own use: a 9:30 visit is a morning visit whatever the
 * column says. Null when the line has no time.
 */
export function periodAt(startMinutes: number | null | undefined): 'Morning' | 'Afternoon' | null {
  if (startMinutes === null || startMinutes === undefined || !Number.isFinite(startMinutes)) return null;
  return startMinutes < 12 * 60 ? 'Morning' : 'Afternoon';
}

/**
 * Whether a visit takes a completion record — the crew's Arrived / Finish — or is simply marked completed (1.6.3).
 * The kind decides first: a staff unit and a facility space never take one (Cleaning Categories, 8 Aug 2026),
 * whatever the record's CompletionRequired says — a unit tagged Staff after its visit was written carried a tenant's
 * flag (apartment 1208, 7 Oct 2026). For a tenant's visit the record's flag decides, as before.
 */
export function takesCompletionRecord(kind: 'tenant' | 'staff' | 'facility', completionRequired: boolean): boolean {
  if (kind === 'staff' || kind === 'facility') return false;
  return completionRequired;
}
